import { Component, Input, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { LLCTransactionIdTag, Tags } from '@core/util/tags';
import { MatterFile } from '@models/matter-file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { Observable } from 'rxjs';
import {
	DownloadDocumentsComponent,
	DownloadLLCDocumentsData,
} from '../download-documents/download-documents.component';
import { format } from 'date-fns';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from 'src/app/shared/state/matter/matter.selectors';

interface UploadedFileVM {
	name: string;
	uploaded: string;
	source: string;
	googleSrcUrl: string;
}

@UntilDestroy()
@Component({
	selector: 'app-llc-downloads-table',
	templateUrl: './llc-downloads-table.component.html',
	standalone: false,
})
export class LlcDownloadsTableComponent implements OnInit {
	public _transactionId: string;
	public _documents: MatterFile[];
	public llcTag = Tags.LLC;
	public transactionTag: string;
	public dataSource: MatTableDataSource<UploadedFileVM>;

	@Select(MatterUploadedFilesSelectors.getAllUploadedFiles)
	allDocuments$: Observable<MatterFile[]>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	@Input() set transactionId(_transactionId: string) {
		this._transactionId = _transactionId;
		this.transactionTag = LLCTransactionIdTag(_transactionId);
		this.filterDocuments();
	}

	@Input() matterMortgageId: string;

	public displayedColumns: string[] = ['name', 'uploaded', 'source'];

	drawerService = inject(DrawerService);

	constructor(private matDialog: MatDialog) {
		this.dataSource = new MatTableDataSource();
	}

	ngOnInit(): void {
		this.allDocuments$?.pipe(untilDestroyed(this)).subscribe((docs) => {
			this._documents = docs;
			this.filterDocuments();
		});
	}

	filterDocuments() {
		if (this._transactionId && this._documents) {
			const _filteredDocs = this._documents.filter(
				(x) =>
					x?.systemTags?.includes(this.llcTag) &&
					x?.systemTags?.includes(this.transactionTag),
			);
			this.dataSource.data = _filteredDocs
				.sort((a, b) => this.sortByUploadedAt(a, b))
				.map((x) => this.buildUploadedFileVM(x));
		}
	}

	trackById = (index: number, item: MatterFile) => item.id;

	buildUploadedFileVM = (matterFile: MatterFile): UploadedFileVM => {
		const _sourceTag = matterFile.systemTags.find((x) => x.includes('source'));
		let source = '';

		if (_sourceTag) {
			const split = _sourceTag.split(':');
			source = split.length >= 2 ? split[1] : '';
		}

		return {
			name: matterFile.name,
			uploaded: format(
				new Date(matterFile.uploadedAt),
				'MMMM do, yyyy h:mm:ss a',
			),
			source,
			googleSrcUrl: matterFile.url,
		};
	};

	sortByUploadedAt(a: MatterFile, b: MatterFile) {
		if (a.uploadedAt > b.uploadedAt) return -1;
		if (a.uploadedAt < b.uploadedAt) return 1;

		return 0;
	}

	download() {
		const data: DownloadLLCDocumentsData = {
			transactionId: this._transactionId,
			matterMortgageId: this.matterMortgageId,
			matterId: this.matterId,
		};

		console.log('Open download dialog', data);

		this.matDialog.open(DownloadDocumentsComponent, { data });
	}

	openFile(data: PdfModalData) {
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: data.fileName,
			noStyle: true,
		});
	}
}
